import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Users, Mail, Phone, Loader2, Trash2, Pencil } from "lucide-react";

export default function PartnersPage() {
  const [partners, setPartners] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", codice_fiscale: "", partita_iva: "", indirizzo: "", provincia: "", referente: "", send_invite: true });
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/partners");
    setPartners(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/partners", form);
      toast.success("Partner creato", {
        description: form.send_invite ? "Email di invito inviata al partner." : "Account partner attivo.",
      });
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", codice_fiscale: "", partita_iva: "", indirizzo: "", provincia: "", referente: "", send_invite: true });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/partners/${p.id}`);
      await load();
      toast.success(p.active ? "Partner disattivato" : "Partner attivato");
    } catch {
      toast.error("Impossibile aggiornare lo stato");
    }
  };

  const openEdit = (p) => {
    setEditForm({
      id: p.id, name: p.name || "", email: p.email || "", phone: p.phone || "",
      codice_fiscale: p.codice_fiscale || "", partita_iva: p.partita_iva || "",
      indirizzo: p.indirizzo || "", provincia: p.provincia || "", referente: p.referente || "", password: "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const { id, password, ...rest } = editForm;
      const payload = password && password.trim() ? { ...rest, password: password.trim() } : rest;
      await api.put(`/partners/${id}`, payload);
      toast.success(password && password.trim() ? "Partner aggiornato · password modificata" : "Partner aggiornato");
      setEditOpen(false);
      setEditForm(null);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setEditSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente il partner "${p.name}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      await load();
      toast.success("Partner eliminato");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight">Gestione Partner</h1>
            <p className="text-sm text-slate-500 mt-1">Invita e gestisci gli account dei partner operativi</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-partner-button" className="gap-2">
                <UserPlus className="h-4 w-4" /> Invita Partner
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Nuovo Partner</DialogTitle>
                <DialogDescription>Crea un account per un partner operativo e, se vuoi, invialo via email.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 pt-2">
                <div>
                  <Label className="nx-label">Ragione Sociale / Nome</Label>
                  <Input data-testid="partner-name" required className="mt-1.5"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="partner-email" type="email" required className="mt-1.5"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Telefono</Label>
                  <Input data-testid="partner-phone" className="mt-1.5"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="nx-label">Codice Fiscale</Label>
                    <Input data-testid="partner-cf" className="mt-1.5"
                      value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} />
                  </div>
                  <div>
                    <Label className="nx-label">Partita IVA</Label>
                    <Input data-testid="partner-piva" className="mt-1.5"
                      value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <div>
                    <Label className="nx-label">Indirizzo</Label>
                    <Input data-testid="partner-indirizzo" className="mt-1.5"
                      value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
                  </div>
                  <div>
                    <Label className="nx-label">Provincia</Label>
                    <Input data-testid="partner-provincia" className="mt-1.5 uppercase" maxLength={2} placeholder="MI"
                      value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} />
                  </div>
                </div>
                <div>
                  <Label className="nx-label">Nominativo Referente</Label>
                  <Input data-testid="partner-referente" className="mt-1.5"
                    value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Password iniziale</Label>
                  <Input data-testid="partner-password" type="text" required minLength={6} className="mt-1.5"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  <p className="text-xs text-slate-400 mt-1">Comunica la password al partner tramite canale sicuro (non viene inviata via email).</p>
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Invia email di invito</p>
                    <p className="text-xs text-slate-500">Notifica al partner che l'account è attivo</p>
                  </div>
                  <Switch data-testid="partner-invite-switch" checked={form.send_invite}
                    onCheckedChange={(v) => setForm({ ...form, send_invite: v })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="partner-save" disabled={saving} className="gap-2 w-full">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea Partner"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 overflow-hidden">
          {partners === null ? (
            <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-12 w-full" />)}</div>
          ) : partners.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun partner registrato. Invita il primo partner.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Servizi</TableHead>
                  <TableHead>Attivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((p) => (
                  <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800 text-sm">{p.name}</p>
                      <p className="text-xs text-slate-400">{p.active ? "Attivo" : p.pending_approval ? <span data-testid={`partner-pending-${p.id}`} className="text-amber-600 font-semibold">In attesa di attivazione</span> : "Disattivato"}{p.provincia && <span data-testid={`partner-provincia-${p.id}`} className="ml-1.5 inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{p.provincia}</span>}</p>
                      {(p.codice_fiscale || p.partita_iva) && (
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {p.codice_fiscale ? `CF ${p.codice_fiscale}` : ""}{p.codice_fiscale && p.partita_iva ? " · " : ""}{p.partita_iva ? `P.IVA ${p.partita_iva}` : ""}
                        </p>
                      )}
                      {p.referente && <p className="text-[11px] text-slate-400">Ref: {p.referente}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {p.email}</span>
                      {p.phone && <span className="flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {p.phone}</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                        {p.assigned_count} assegnati
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Switch data-testid={`partner-toggle-${p.id}`} checked={p.active} onCheckedChange={() => toggle(p)} />
                        <Button data-testid={`partner-edit-${p.id}`} variant="ghost" size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-primary"
                          title="Modifica partner" onClick={() => openEdit(p)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button data-testid={`partner-delete-${p.id}`} variant="ghost" size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                          title="Elimina partner" onClick={() => remove(p)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditForm(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Modifica Partner</DialogTitle>
              <DialogDescription>Aggiorna i dati anagrafici e di contatto del partner.</DialogDescription>
            </DialogHeader>
            {editForm && (
              <form onSubmit={saveEdit} className="space-y-4 pt-2">
                <div>
                  <Label className="nx-label">Ragione Sociale / Nome</Label>
                  <Input data-testid="partner-edit-name" required className="mt-1.5"
                    value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="partner-edit-email" type="email" required className="mt-1.5"
                    value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Telefono</Label>
                  <Input data-testid="partner-edit-phone" className="mt-1.5"
                    value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="nx-label">Codice Fiscale</Label>
                    <Input data-testid="partner-edit-cf" className="mt-1.5"
                      value={editForm.codice_fiscale} onChange={(e) => setEditForm({ ...editForm, codice_fiscale: e.target.value })} />
                  </div>
                  <div>
                    <Label className="nx-label">Partita IVA</Label>
                    <Input data-testid="partner-edit-piva" className="mt-1.5"
                      value={editForm.partita_iva} onChange={(e) => setEditForm({ ...editForm, partita_iva: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <div>
                    <Label className="nx-label">Indirizzo</Label>
                    <Input data-testid="partner-edit-indirizzo" className="mt-1.5"
                      value={editForm.indirizzo} onChange={(e) => setEditForm({ ...editForm, indirizzo: e.target.value })} />
                  </div>
                  <div>
                    <Label className="nx-label">Provincia</Label>
                    <Input data-testid="partner-edit-provincia" className="mt-1.5 uppercase" maxLength={2} placeholder="MI"
                      value={editForm.provincia} onChange={(e) => setEditForm({ ...editForm, provincia: e.target.value.toUpperCase() })} />
                  </div>
                </div>
                <div>
                  <Label className="nx-label">Nominativo Referente</Label>
                  <Input data-testid="partner-edit-referente" className="mt-1.5"
                    value={editForm.referente} onChange={(e) => setEditForm({ ...editForm, referente: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Nuova Password</Label>
                  <Input data-testid="partner-edit-password" type="text" minLength={6} className="mt-1.5"
                    placeholder="Lascia vuoto per non modificarla"
                    value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                  <p className="text-xs text-slate-400 mt-1">Imposta una nuova password per il partner (min. 6 caratteri). Comunicala tramite canale sicuro.</p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="partner-edit-save" disabled={editSaving} className="gap-2 w-full">
                    {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Modifiche"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
